import type { Vector3Plain } from "../state";

/**
 * The in-progress half of the measure tool: either nothing is pinned yet,
 * or one anchor is waiting for its second point.
 */
export type MeasureSession =
  { phase: "empty" } | { phase: "anchored"; start: Vector3Plain };

/** One completed point-to-point measurement, in data space. */
export interface Measurement {
  /**
   * Stable for the measurement's lifetime and never reused, even across a
   * clear. `renderMeasures` and the React overlay both key on it, and the
   * scene visual bakes its line geometry at build time, so a reused id
   * would inherit the previous measurement's line.
   */
  id: number;
  start: Vector3Plain;
  end: Vector3Plain;
  /** Euclidean distance between the endpoints, in metres. */
  length: number;
}

/** The measure tool's full state: the session plus completed measurements. */
export interface MeasureState {
  session: MeasureSession;
  measurements: Measurement[];
  /** The id the next completed measurement takes. */
  nextId: number;
}

/**
 * A fresh measure state with the id counter at 0. Only safe where no
 * measurement has been drawn yet; to empty an existing state, use
 * `clearMeasurements`, which keeps the counter.
 */
export function emptyMeasureState(): MeasureState {
  return { session: { phase: "empty" }, measurements: [], nextId: 0 };
}

/**
 * Applies one pick. The first pin anchors; the second completes a
 * measurement and re-arms for the next. A second pin exactly equal to the
 * anchor returns the state unchanged, since a zero-length measurement
 * would only be noise. Pure: never mutates the input state.
 */
export function pinPoint(
  state: MeasureState,
  point: Vector3Plain,
): MeasureState {
  if (state.session.phase === "empty") {
    return { ...state, session: { phase: "anchored", start: point } };
  }
  const start = state.session.start;
  if (start.x === point.x && start.y === point.y && start.z === point.z) {
    return state;
  }
  const length = Math.hypot(
    point.x - start.x,
    point.y - start.y,
    point.z - start.z,
  );
  return {
    session: { phase: "empty" },
    measurements: [
      ...state.measurements,
      { id: state.nextId, start, end: point, length },
    ],
    nextId: state.nextId + 1,
  };
}

/**
 * Drops every measurement and any in-progress anchor. Keeps the id
 * counter, so nothing pinned afterwards reuses a cleared id.
 */
export function clearMeasurements(state: MeasureState): MeasureState {
  return { ...state, session: { phase: "empty" }, measurements: [] };
}

/**
 * Drops one completed measurement, leaving the others and any in-progress
 * anchor alone. Survivors keep the ids they were given, so the labels the
 * user is reading do not renumber under them, and the id counter is
 * untouched, so nothing pinned afterwards reuses the removed id. An id
 * matching nothing returns the state unchanged. Pure: never mutates the
 * input state.
 */
export function removeMeasurement(
  state: MeasureState,
  id: number,
): MeasureState {
  const measurements = state.measurements.filter(
    (measurement) => measurement.id !== id,
  );
  if (measurements.length === state.measurements.length) {
    return state;
  }
  return { ...state, measurements };
}
